export default {
  slug: "what-is-a-hyrox-class",
  metaTitle: "What Is a HYROX Class? Inside a 90-Minute Session",
  metaDescription:
    "A HYROX class is a coached session built around the race: compromised running, station strength and pacing. Here's what a 90-minute Dallas specialty class covers.",
  h1: "What Is a HYROX Class? Inside a 90-Minute Specialty Session",
  category: "Classes & Booking",
  pillar: false,
  datePublished: "2026-10-06",
  dateModified: "2026-10-06",
  readTime: 7,
  hero: {
    src: "/dps/bg-program.jpg",
    alt: "Athletes pushing sleds in a row during a HYROX class in Dallas",
    caption: "Sled lanes mid-class at Dallas Performance Systems.",
  },
  summary:
    "A HYROX class is a coached training session built around the specific demands of the HYROX race: station strength endurance, heart-rate control and compromised running. In Dallas, specialty classes run 90 minutes, follow a 12-week periodized program, and cost $20.",
  takeaways: [
    "A HYROX class trains the race's actual stations at (or scaled from) official standards, not generic circuits.",
    "The signature ingredient is compromised running: learning to run 1km immediately after heavy station work.",
    'Dallas classes follow the <a href="/program">12-week program</a>, so sessions change with the training stage.',
    "Any fitness level can start; loads and pace scale while the standard stays visible.",
  ],
  body: [
    { t: "p", html: "\u201CHYROX class\u201D gets used loosely. Some gyms mean a themed bootcamp; a real one means training built backwards from the race. Here's what actually happens inside our 90 minutes in Dallas, and how to tell the difference anywhere." },

    { t: "h2", id: "definition", text: "What is a HYROX class?" },
    { t: "p", html: "A HYROX class is a coached group session that targets the five demands of the race: muscle endurance, strength under fatigue, heart-rate management, station skill, and the ability to keep running when your legs are loaded. Everything in the session maps to one of those five. If a movement doesn't show up in the race or build capacity for it, it doesn't make the class." },

    { t: "h2", id: "inside-90", text: "Inside the 90 minutes" },
    { t: "p", html: "The exact session changes with the program stage, but the skeleton holds. Here's a typical layout:" },
    {
      t: "table",
      head: ["Segment", "Time", "What happens"],
      rows: [
        ["Warm-up & standards", "0–15 min", "Movement prep plus a standards walk-through for the day's stations"],
        ["Strength endurance block", "15–40 min", "Loaded work: sleds, carries, lunges at or near division weights"],
        ["Compromised running", "40–70 min", "Run intervals paired directly off station efforts, the core race skill"],
        ["Station skill / finisher", "70–85 min", "Technique reps where standards bite: wall balls, SkiErg, burpee broad jumps"],
        ["Cooldown & debrief", "85–90 min", "What the block builds toward and what to touch before next class"],
      ],
      note: "Typical structure; segments shift with the 12-week program stage.",
    },

    { t: "h2", id: "vs-regular", text: "How it differs from a regular gym class" },
    {
      t: "ul",
      items: [
        "<span><strong>Fixed target:</strong> the race never changes, so training is specific instead of random.</span>",
        "<span><strong>Standards coached:</strong> sled fully across, knee touch every lunge, depth every wall ball. Reps that wouldn't count in a race don't count in class.</span>",
        "<span><strong>Running is in the workout,</strong> not a warm-up. Pairing runs with loaded work is the single biggest gap in most athletes' prep.</span>",
        "<span><strong>Periodized:</strong> week 2 and week 10 train differently on purpose.</span>",
      ],
    },

    { t: "h2", id: "program", text: "How classes follow the 12-week program" },
    { t: "p", html: 'Every class slots into the <a href="/program">12-week Dallas program</a>: Base builds the engine, Pace and Accelerate sharpen it, Prime and Race Week peak it. You can join at any stage; the coach scales you into wherever the block is. The payoff of the whole cycle is the <a href="/blog/what-is-a-hyrox-simulation">full simulation</a>, where the training gets measured.' },

    { t: "h2", id: "who-for", text: "Who HYROX classes are for" },
    { t: "p", html: "Racers inside a build, obviously. But roughly half of a given Dallas class isn't registered for any race. They're there because the format is the most honest group training available: measurable, scalable, and never random. If you can walk in the door, you can be scaled into the session." },
    { t: "p", html: 'Not sure whether to start with a coached class or the free Sunday open session? The honest comparison is in <a href="/blog/hyrox-open-session-vs-specialty-class">open session versus specialty class</a>.' },

    { t: "h2", id: "home", text: "Touching the stations between classes" },
    { t: "disclosure" },
    { t: "p", html: 'You don\'t need a home gym to benefit from class, but two pieces compound it fast: a race-spec <a href="https://tidd.ly/4pfPunk" rel="sponsored noopener" target="_blank">sandbag</a> for lunges and carries, and <a href="https://tidd.ly/45L0JwH" rel="sponsored noopener" target="_blank">kettlebells</a> at your division\'s farmers-carry weight. More on the <a href="/gear">gear page</a>.' },
  ],
  faqs: [
    { q: "How fit do I need to be for a HYROX class?", a: "Fit enough to move for 90 minutes with rest built in, which is most people. Loads and run paces scale to you; the coach holds the standard, not the stopwatch." },
    { q: "How many classes a week should I take?", a: "One is the floor, two is the sweet spot for a 12-week build. Pair them with the free Sunday session and you have a complete week." },
    { q: "How much does a class cost?", a: 'Classes are $20 drop-in or $60 for a 4-pack. The full price breakdown is in <a href="/blog/hyrox-training-cost-dallas">what HYROX training costs in Dallas</a>.' },
    { q: "Where and when do classes run?", a: 'Fridays 6:00 PM and Saturdays 11:30 AM at Dallas Performance Systems, 2651 Fondren Dr, Ste. B. Booking details are in the guide to <a href="/blog/hyrox-classes-near-me-dallas">HYROX classes in Dallas</a> and on the <a href="/schedule">schedule page</a>.' },
  ],
  sources: [
    "Hyrox Ninja class structure and 12-week program — hyroxninja.com/program",
    "HYROX official race format — hyrox.com",
  ],
  related: ["hyrox-classes-near-me-dallas", "hyrox-training-cost-dallas", "hyrox-open-session-vs-specialty-class"],
  cta: {
    heading: "Experience one for $20",
    html: "The fastest way to understand a HYROX class is 90 minutes inside one. Friday 6:00 PM or Saturday 11:30 AM.",
    to: "/schedule",
    label: "Book a Class",
  },
};
